import type { CSSProperties, ReactElement } from 'react'
import { IconRouteBadgeBody } from './icons/IconRouteBadgeBody'
import styles from './RouteBadge.module.css'

export interface RouteBadgeProps {
  /** The endpoint letter — "A" (from) or "B" (to). */
  readonly letter: 'A' | 'B'
  /** Hex color of the endpoint station's line. Tints the pin body. DATA, not a token. */
  readonly lineHexColor: string
}

/**
 * Map pin marking a selected route endpoint (Figma spec §1.6). 50×56 pin
 * — a rounded body + down-arrow tip whose point lands at the station
 * coordinate — with the endpoint letter centered in `boldPx18` white.
 *
 * The pin body ({@link IconRouteBadgeBody}) fill is bound to
 * `currentColor`; we set `color: lineHexColor` on the wrapper so the
 * body tints per line color (line color is DATA). Drop-shadow is
 * `0 4px 3px rgba(0,0,0,0.2)` (Figma blur 3, not "r6" — spec Drift #2).
 *
 * Rendered as a map overlay above the dim layer for route-built states;
 * coexists with {@link UserLocationDot}.
 */
export function RouteBadge({ letter, lineHexColor }: RouteBadgeProps): ReactElement {
  const tint: CSSProperties = { color: lineHexColor }
  return (
    <div
      className={styles.root}
      style={tint}
      data-testid={`route-bubble-${letter}`}
      data-letter={letter}
      aria-hidden="true"
    >
      <IconRouteBadgeBody className={styles.body} width="50" height="56" />
      <span className={styles.letter}>{letter}</span>
    </div>
  )
}
